import React from 'react';
import Text from '@amzn/meridian/text';
import Column from '@amzn/meridian/column';
import Breadcrumb, { BreadcrumbGroup } from '@amzn/meridian/breadcrumb';
import Responsive from '@amzn/meridian/responsive';
import SearchInput from 'src/components/common/search-input/SearchInput';
import UILoader from 'src/components/common/loader/UILoaderContainer';
import StatusAlert from 'src/components/common/status-alert/StatusAlertContainer';
import * as AppStrings from 'src/localization/string-ids/app-strings';
import * as AppRoutes from 'src/routing/constants/routes';
import { responsiveProps } from 'src/constants/responsiveProps';
import { buildGetContainerDetailsInput, GetContainerDetailsInput, GetContainerMode } from 'src/models/GetContainerDetails';
import { ContainerDetailsModel } from 'src/components/common/container-details/ContainerDetailsModel';
import { ScanIcons } from 'src/constants/general-media';
import { AlertLevels } from 'src/models/alert-model';
import { UIAlertState } from 'src/store/common-slices/uiSlice';
import './JackpotScanTote.scss';

interface JackpotScanToteProps {
    containerDetails?: ContainerDetailsModel;
    uiAlertState: UIAlertState;
    warehouseId: string;
    associate: string;
    locale: string;
    fetchContainerDetails: (getContainerDetailsInput: GetContainerDetailsInput) => Promise<unknown>;
    onToteScanned: (containerId: string, toteDetails: ContainerDetailsModel) => void;
}

class JackpotScanTote extends React.Component<JackpotScanToteProps> {

    private onToteSubmit = async (containerId: string) => {
        if (!containerId.trim()) {
            return;
        }
        await this.props.fetchContainerDetails(buildGetContainerDetailsInput(containerId,
            this.props.warehouseId, this.props.associate, this.props.locale,
            GetContainerMode.SEARCH));

        // Only advance to Scan Item when the fetch actually succeeded — on error, the error
        // alert (rendered below via StatusAlert) stays on this page so the associate can retry.
        // fetchContainerDetails never sets alertLevel to SUCCESS on success, only to ERROR on
        // failure (and resets it to UNDEFINED beforehand), so ERROR is the one state to block on.
        if (this.props.uiAlertState.alertLevel === AlertLevels.ERROR) {
            return;
        }
        const toteDetails = this.props.containerDetails;
        if (!toteDetails) {
            return;
        }
        this.props.onToteScanned(containerId, toteDetails);
    };

    public render() {
        return (
            <Responsive query={"max-width"} props={responsiveProps}>
                {props => (
                    <Column className="scan-tote">
                        <BreadcrumbGroup>
                            <Breadcrumb href={AppRoutes.HOME_PAGE_ROUTE}>{AppStrings.HOME}</Breadcrumb>
                            <Breadcrumb>{AppStrings.JACKPOT}</Breadcrumb>
                        </BreadcrumbGroup>
                        <Column alignmentHorizontal="center">
                            <img src={ScanIcons.SCAN_HANDHELD_ICON} alt="Scan Icon"
                                 className="scan-tote-icon"/>
                            <Text type={props.titleSize} data-testid="jackpot-scan-tote-heading">{AppStrings.SCAN_TOTE_HEADING}</Text>
                        </Column>
                        <SearchInput
                            searchFieldLabel={AppStrings.SCAN_TOTE_SEARCHFIELD_LABEL}
                            searchFieldPlaceholder={AppStrings.SCAN_TOTE_SEARCHFIELD_PLACEHOLDER}
                            renderSearchButton={true}
                            onSubmit={this.onToteSubmit}
                            data-testid="jackpot-scan-tote-search-input"
                        />
                        <UILoader/>
                        <StatusAlert/>
                    </Column>
                )}
            </Responsive>
        );
    }
}

export default JackpotScanTote;
